/* eslint-disable no-console */
import {
  call,
  cancel,
  cancelled,
  fork,
  ForkEffect,
  put,
  take,
} from 'redux-saga/effects';

import { abortApiRequest } from 'api/requestCancellation';
import { HTTP_CODES } from 'lib/constants';

import ActionConstants from '../constants';
import store from '../store';

export const ASSISTED_VKYC_REQUEST_CANCELLED =
  'Assisted vKYC request was cancelled';

const invokeCallback = (callback, value) => {
  if (!callback) return;

  try {
    callback(value);
  } catch (error) {
    console.error('Assisted vKYC callback failed', error);
  }
};

const getResponseBody = (response) => {
  if (response?.body) return response.body;
  if (response?.text) return JSON.parse(response.text);
  return {};
};

const getFailureBody = (error) =>
  error?.response?.body || {
    code: error?.response?.statusCode || error?.status || 0,
    message: error?.message || 'Assisted vKYC request failed',
  };

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* assistedVkycWorker(action) {
  const {
    serviceMethod,
    actionTypeSuccess,
    actionTypeFailure,
    actionTypeRequest,
    extra,
    cancelKey,
    successCallback,
    failureCallback,
  } = action.payload;
  let callbackSettled = false;

  try {
    yield put({ type: actionTypeRequest, payload: extra });
    const response = yield call(serviceMethod);
    const body = getResponseBody(response);

    yield put({
      type: actionTypeSuccess,
      payload: { body, extra },
    });
    invokeCallback(successCallback, body);
    callbackSettled = true;
  } catch (error) {
    const statusCode = error?.response?.statusCode || error?.status;
    const shouldForceLogout =
      (store.getState().loginReducer.loginState.isUserAuthenticated &&
        statusCode === HTTP_CODES.FORBIDDEN_CODE) ||
      statusCode === HTTP_CODES.UNAUTHORIZED_CODE;

    if (shouldForceLogout) {
      yield put({
        type: ActionConstants.POST_LOGOUT_SUCCESS,
        payload: { body: true },
      });
    }
    yield put({
      type: actionTypeFailure,
      payload: { body: getFailureBody(error), extra },
    });
    invokeCallback(failureCallback, error);
    callbackSettled = true;
  } finally {
    if (yield cancelled()) {
      abortApiRequest(cancelKey);
      const cancellationError = Object.assign(
        new Error(ASSISTED_VKYC_REQUEST_CANCELLED),
        { isCancelled: true }
      );

      yield put({
        type: actionTypeFailure,
        payload: {
          body: { code: 0, message: ASSISTED_VKYC_REQUEST_CANCELLED },
          extra,
        },
      });
      if (!callbackSettled) {
        invokeCallback(failureCallback, cancellationError);
        callbackSettled = true;
      }
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* takeLatestUntilAssistedClear(actionType) {
  let lastTask;

  while (true) {
    const action = yield take([
      actionType,
      ActionConstants.CLEAR_ASSISTED_VKYC_STATE,
    ]);

    if (action.type === ActionConstants.CLEAR_ASSISTED_VKYC_STATE) {
      if (lastTask) {
        yield cancel(lastTask);
        lastTask = null;
      }
      continue;
    }

    if (lastTask) yield cancel(lastTask);
    lastTask = yield fork(assistedVkycWorker, action);
  }
}

export default function* assistedVkycWatcher(): Generator<
  ForkEffect<void>,
  void,
  unknown
> {
  yield fork(
    takeLatestUntilAssistedClear,
    ActionConstants.GET_ASSISTED_VKYC_STATUS
  );
  yield fork(
    takeLatestUntilAssistedClear,
    ActionConstants.GET_ASSISTED_KYC_DATA
  );
  yield fork(
    takeLatestUntilAssistedClear,
    ActionConstants.UPDATE_ASSISTED_SIGNATORY_DETAILS
  );
  yield fork(
    takeLatestUntilAssistedClear,
    ActionConstants.INITIATE_ASSISTED_VKYC
  );
}
